import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Paper,
  Avatar,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WebIcon from '@mui/icons-material/Web';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import SpeedIcon from '@mui/icons-material/Speed';
import BrushIcon from '@mui/icons-material/Brush';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const services = [
  {
    icon: <WebIcon />,
    title: 'Shopify Development',
    description: 'Custom Shopify solutions to help grow your e-commerce business',
    features: [
      'Native iOS development with Swift and SwiftUI',
      'Android development with Kotlin and Java',
      'Cross-platform solutions when appropriate',
      'App Store and Play Store deployment',
      'Regular updates and maintenance',
    ],
    color: '#14b8a6',
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
  },
  {
    icon: <PhoneIphoneIcon />,
    title: 'Mobile App Development',
    description: 'Native iOS and Android applications built with the latest technologies',
    features: [
      'Native iOS development with Swift and SwiftUI',
      'Android development with Kotlin and Java',
      'Cross-platform solutions when appropriate',
      'App Store and Play Store deployment',
      'Regular updates and maintenance',
    ],
    color: '#14b8a6',
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
  },
  {
    icon: <WebIcon />,
    title: 'Web Development',
    description: 'Modern, responsive web applications using React and Next.js',
    features: [
      'React.js and Next.js development',
      'Responsive design for all devices',
      'Progressive Web Apps (PWA)',
      'SEO optimization',
      'Performance optimization',
    ],
    color: '#f97316',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  },
  {
    icon: <StorageIcon />,
    title: 'Backend Development',
    description: 'Robust and scalable backend solutions for your applications',
    features: [
      'RESTful API development',
      'Database design and optimization',
      'Authentication and authorization',
      'Third-party API integration',
      'Microservices architecture',
    ],
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  },
  {
    icon: <CloudIcon />,
    title: 'Cloud Solutions',
    description: 'Cloud infrastructure setup and management',
    features: [
      'AWS cloud services',
      'Firebase integration',
      'Server setup and maintenance',
      'Cloud storage solutions',
      'Automated deployments',
    ],
    color: '#0891b2',
    gradient: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
  },
  {
    icon: <SpeedIcon />,
    title: 'Optimization',
    description: 'Optimize your existing applications for better performance',
    features: [
      'Code optimization',
      'Database query optimization',
      'Caching implementation',
      'Load time improvement',
      'Resource optimization',
    ],
    color: '#dc2626',
    gradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
  }
];

const Services = () => {
  const theme = useTheme();

  return (
    <>
      <SEOHead
        title="Professional Development Services - DevHopes Labs"
        description="Comprehensive mobile and web development services including Shopify development, iOS apps, Android apps, React.js web applications, and cloud solutions. Expert development services tailored to your business needs."
        keywords="mobile app development services, web development services, Shopify development, iOS app development, Android app development, React.js development, Next.js development, cloud services, AWS consulting, custom software development, professional development services, Netherlands"
        url="/services"
        type="website"
        schemaType="Service"
        service={{
          name: "Professional Development Services",
          description: "Comprehensive mobile and web development services including Shopify development, iOS apps, Android apps, React.js web applications, and cloud solutions.",
          type: "Software Development",
          areaServed: ["Netherlands", "Europe", "Worldwide"],
          offers: [
            {
              "@type": "Offer",
              name: "Mobile App Development",
              description: "Native iOS and Android app development"
            },
            {
              "@type": "Offer", 
              name: "Web Development",
              description: "Modern React.js and Next.js web applications"
            },
            {
              "@type": "Offer",
              name: "Shopify Development", 
              description: "Custom Shopify solutions and apps"
            },
            {
              "@type": "Offer",
              name: "Cloud Services",
              description: "AWS cloud infrastructure and deployment"
            }
          ]
        }}
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)',
          py: { xs: 10, md: 12 },
          mt: { xs: 7, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          },
        }}
      >
        <Container maxWidth="lg">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                mb: 3,
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                maxWidth: 700,
                mx: 'auto',
                color: 'black',
                fontWeight: 300,
                lineHeight: 1.6,
              }}
            >
              Comprehensive development solutions to help your business succeed in the digital world
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* Services Grid */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                {/* Header with gradient */}
                <Box
                    sx={{
                      background: service.gradient,
                      p: 4,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 1,
                        background: 'rgba(255,255,255,0.2)',
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        mb: 3,
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.3)',
                      }}
                    >
                      {React.cloneElement(service.icon, {
                        sx: { fontSize: 32, color: 'white' }
                      })}
                    </Avatar>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: 'white',
                        mb: 2,
                        fontSize: '1.5rem',
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.6,
                        fontSize: '1rem',
                      }}
                    >
                      {service.description}
                    </Typography>
                  </Box>

                  {/* Content */}
                  <Box sx={{ p: 4 }}>
                
                    <List sx={{ p: 0, mb: 3 }}>
                      {service.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon 
                              sx={{ 
                                color: service.color,
                                fontSize: 20,
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)',
          color: 'white',
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Need a Custom Solution?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 300, color: 'black' }}>
              Let's discuss your specific requirements and create a tailored solution for your business
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'white',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Contact Me
            </Button>
        </Container>
      </Box>
    </>
  );
};

export default Services; 